import React, { useState } from 'react'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { IconButton, TableCell as MuiTableCell, TableRow as MuiTableRow } from '@mui/material'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'

import TableCell from 'components/table/TableCell'
import type { ColumnConfig } from 'components/table/types'

type TableRowProps = {
  config: ColumnConfig[]
  data: Record<string, any>
  hasExpandableItems: boolean
  index: number
  getRowLink?: (...args: any[]) => any
  hover?: boolean
  renderDetailView?: (...args: any[]) => any
  onRowClick?: (...args: any[]) => any
}

// .MuiButtonBase-root covers the MUI controls that render as a <span> (Checkbox, Switch, Radio).
const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], .MuiButtonBase-root'

const isInteractiveTarget = (event: React.MouseEvent) => !!(event.target as Element)?.closest?.(INTERACTIVE_SELECTOR)

const TableRow = ({
  config,
  data,
  getRowLink,
  hasExpandableItems,
  onRowClick,
  hover,
  index,
  renderDetailView,
}: TableRowProps) => {
  const [expanded, setExpanded] = useState(false)
  const navigate = useNavigate()
  const rowLink = getRowLink ? getRowLink(data) : null

  // Navigate on row click rather than row-as-<a>: TableRow component={Link} yields an <a> inside <tbody>, which is invalid HTML.
  const handleRow = onRowClick ? onRowClick(data) : rowLink ? () => navigate(rowLink) : undefined

  const props = {
    className: classNames('table__row', { 'table__row--link': !!rowLink || !!onRowClick }),
    // A row-wide onClick would otherwise swallow clicks meant for a control inside a cell.
    onClick:
      handleRow &&
      ((event: React.MouseEvent) => {
        if (!isInteractiveTarget(event)) handleRow(event)
      }),
  }

  const detailView = renderDetailView?.(data, index)

  return (
    <>
      <MuiTableRow {...props} hover={hover}>
        {hasExpandableItems && (
          <MuiTableCell>
            {!!detailView && (
              <IconButton aria-label="Toggle row details" onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <ExpandLessIcon fontSize="medium" /> : <ChevronRightIcon fontSize="medium" />}
              </IconButton>
            )}
          </MuiTableCell>
        )}

        {config.map((cell, index: number) => (
          <TableCell cell={cell} data={data} index={index} key={index} />
        ))}
      </MuiTableRow>
      {expanded && (
        <MuiTableRow component="tr">
          <MuiTableCell colSpan={config.length + 1} component="td" style={{ verticalAlign: 'top' }}>
            {detailView}
          </MuiTableCell>
        </MuiTableRow>
      )}
    </>
  )
}

export default TableRow
